// frontend/src/pages/OwnerDashboard.js
//
// Page /owner — dashboard owner Revyo (Florian + Adrien).
//
// Auth (v2 — 2026-05-12) :
//  - Login user standard (JWT en localStorage clé "token")
//  - Whitelist email côté backend (env OWNER_EMAILS dans Railway)
//  - Pas de saisie de token — l'utilisateur se connecte sur /login puis vient ici.
//
// Données : GET /api/owner/dashboard (toutes les métriques en un seul appel),
//           auto-refresh 60s.

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';
import { tokens } from '../styles/design-tokens';
import Chart from 'chart.js/auto';
import { storage } from '../utils/platform';
import { apiGet, apiPost } from '../utils/api-client';

const JWT_KEY = 'token'; // clé localStorage standard utilisée par tout le reste de l'app

/* ═══════════════════════════════════════════════════════════════════════════
 * Onglets du dashboard owner (01/08/2026)
 * ───────────────────────────────────────────────────────────────────────────
 * La page empilait douze blocs sur un seul écran : illisible, et il fallait
 * scroller longtemps pour atteindre le pilotage ou la gestion des comptes.
 * Chaque bloc a désormais son onglet, et l'accueil ne montre que ce qui se
 * regarde tous les jours : les 4 KPI et ce qu'il y a à traiter.
 *
 * L'onglet actif vit dans l'URL (?tab=sante) : un rafraîchissement ou un lien
 * partagé rouvre la même vue.
 * ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════
 * Affichage mobile (01/08/2026)
 * ───────────────────────────────────────────────────────────────────────────
 * Les styles de cette page sont des objets JS inline, qui NE SUPPORTENT PAS
 * les media queries. D'où cette feuille injectée : c'est le seul moyen d'avoir
 * un vrai responsive sans réécrire toute la page en CSS modules.
 *
 * Parti pris pour les tableaux : en dessous de 700 px, chaque ligne devient une
 * CARTE empilée, l'en-tête est masqué et chaque cellule affiche son libellé via
 * data-label. Un simple scroll horizontal aurait été plus rapide à écrire, mais
 * il oblige à balayer latéralement pour lire une ligne — exactement ce qu'on
 * cherche à éviter en portrait.
 * ═══════════════════════════════════════════════════════════════════════════ */
const MOBILE_CSS = `
@media (max-width: 700px) {
  .ow-page { padding: 12px !important; }

  /* Onglets : défilement horizontal plutôt qu'un pavé de 7 boutons sur 3 lignes,
     qui mangerait le haut de l'écran. */
  .ow-tabbar {
    flex-wrap: nowrap !important;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    margin-left: -12px; margin-right: -12px;
    padding-left: 12px; padding-right: 12px;
  }
  .ow-tabbar::-webkit-scrollbar { display: none; }
  .ow-tabbar button { flex: 0 0 auto; font-size: 12.5px !important; padding: 7px 12px !important; }

  /* Grilles : 2 colonnes pour les compteurs, 1 pour les grandes cartes. */
  .ow-minigrid { grid-template-columns: repeat(2, 1fr) !important; }
  .ow-heroes,
  .ow-row { grid-template-columns: 1fr !important; }

  .ow-card { padding: 14px !important; }

  /* Tableaux → cartes empilées */
  .ow-table thead { display: none; }
  .ow-table, .ow-table tbody, .ow-table tr, .ow-table td { display: block; width: 100%; }
  .ow-table tr {
    border: 1px solid rgba(0,0,0,0.08);
    border-radius: 10px;
    padding: 6px 10px;
    margin-bottom: 10px;
    background: rgba(0,0,0,0.015);
  }
  .ow-table td {
    display: flex !important;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    text-align: right !important;
    border-bottom: 1px solid rgba(0,0,0,0.05) !important;
    padding: 7px 0 !important;
  }
  .ow-table tr td:last-child { border-bottom: none !important; }
  /* Le libellé de colonne, repris de data-label, remplace l'en-tête masqué. */
  .ow-table td::before {
    content: attr(data-label);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8A867C;
    font-weight: 700;
    text-align: left;
    flex: 0 0 auto;
    padding-right: 8px;
  }
  /* Cellule sans libellé (ligne « aucun résultat ») : pas de faux en-tête. */
  .ow-table td:not([data-label])::before { content: none; }

  /* Les zones d'action passent en pleine largeur : viser un bouton de 40 px
     entre deux colonnes au pouce est pénible. */
  .ow-table td[data-label="Actions"],
  .ow-table td[data-label="Action"] { flex-wrap: wrap; justify-content: flex-end; }
  .ow-table button { min-height: 34px; }

  /* Champs et boutons : 16px minimum sur iOS, sinon Safari zoome au focus. */
  .ow-input { font-size: 16px !important; width: 100% !important; }
  .ow-fullwidth { width: 100% !important; }
}
`;

const TABS = [
  { id: 'apercu',    label: "Vue d'ensemble",       emoji: '📊' },
  // Revenus ET coûts variables ensemble : les deux faces de la marge se lisent
  // mal séparées, et « Coûts » seul ne pesait que trois chiffres.
  { id: 'revenu',    label: 'Revenu & coûts',       emoji: '💳' },
  { id: 'sante',     label: 'Santé des comptes',    emoji: '🩺' },
  // Activité des restaurateurs (connexions) ET de leurs clients (funnel QR) :
  // c'est la même question — le produit sert-il ? Les funnels étaient rangés
  // dans « Technique », où personne ne va chercher une métrique de conversion.
  { id: 'activite',  label: 'Activité & funnel',    emoji: '👥' },
  { id: 'comptes',   label: 'Gestion des comptes',  emoji: '🗂️' },
  { id: 'piloter',   label: 'Piloter un restaurant', emoji: '🎛️' },
  { id: 'technique', label: 'Technique',            emoji: '🔧' },
];
const DEFAULT_TAB = 'apercu';

// Onglets supprimés lors de la fusion → où atterrissent leurs anciens liens.
// Sans cela, un favori ou un lien partagé sur ?tab=couts retomberait
// silencieusement sur la vue d'ensemble.
const TAB_ALIASES = { couts: 'revenu' };

function readTabFromUrl() {
  if (typeof window === 'undefined') return DEFAULT_TAB;
  const raw = new URLSearchParams(window.location.search).get('tab');
  const t = TAB_ALIASES[raw] || raw;
  return TABS.some(x => x.id === t) ? t : DEFAULT_TAB;
}
const REFRESH_MS = 60_000;

const LANG_META = {
  fr: { flag: '🇫🇷', label: 'Français' },
  en: { flag: '🇬🇧', label: 'English' },
  es: { flag: '🇪🇸', label: 'Español' },
  it: { flag: '🇮🇹', label: 'Italiano' }
};
function flagFor(lang) { return (LANG_META[lang] || {}).flag || '🏳️'; }
function labelFor(lang) { return (LANG_META[lang] || {}).label || (lang || '—'); }

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [jwt, setJwt] = useState(() => {
    try { return localStorage.getItem(JWT_KEY) || ''; } catch { return ''; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const chartRef = useRef(null);
  const [activeTab, setActiveTab] = useState(readTabFromUrl);

  // L'onglet est reflété dans l'URL sans empiler d'entrées d'historique :
  // le bouton « retour » du navigateur doit sortir du dashboard, pas
  // rejouer une à une les vues consultées.
  const selectTab = useCallback((tabId) => {
    setActiveTab(tabId);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tabId);
      window.history.replaceState({}, '', url);
    } catch { /* URL indisponible — sans effet sur l'affichage */ }
  }, []);
  const chartInstanceRef = useRef(null);

  // --- Pilotage : liste de tous les restos pour permettre d'en choisir un ---
  const [restos, setRestos] = useState([]);
  const [restosLoading, setRestosLoading] = useState(false);
  const [restosError, setRestosError] = useState(null);
  const [search, setSearch] = useState('');
  const [impersonating, setImpersonating] = useState(false); // état UI bouton "Piloter"
  const [impersonateError, setImpersonateError] = useState(null);

  // ------- Data loading -------
  const loadData = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/owner/dashboard`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Accept': 'application/json'
        }
      });
      if (res.status === 401) {
        // JWT expiré ou invalide → on déconnecte et redirige vers login
        try { localStorage.removeItem(JWT_KEY); } catch {}
        setJwt('');
        navigate('/login?next=/owner');
        return;
      }
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      if (res.status === 503) {
        const j = await res.json().catch(() => ({}));
        setError(j.hint || j.error || 'Backend pas configuré (OWNER_EMAILS absent).');
        return;
      }
      if (!res.ok) {
        setError(`Erreur HTTP ${res.status}`);
        return;
      }
      const json = await res.json();
      setData(json);
      setForbidden(false);
      setLastFetchAt(new Date());
    } catch (e) {
      setError(`Impossible de joindre l'API Revyo : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [jwt, navigate]);

  // Redirection /login si pas connecté du tout
  useEffect(() => {
    if (!jwt) navigate('/login?next=/owner');
  }, [jwt, navigate]);

  useEffect(() => { if (jwt) loadData(); }, [jwt, loadData]);

  useEffect(() => {
    if (!jwt) return;
    const id = setInterval(loadData, REFRESH_MS);
    return () => clearInterval(id);
  }, [jwt, loadData]);

  // ------- Liste complète des restos (pour la section "Piloter") -------
  // Chargée une fois au mount (LIMIT 100 côté backend), filtrée client-side
  // par `search`. Pas de refetch sur changement de search → suffit largement
  // tant qu'on est sous les 100 restos.
  const loadRestos = useCallback(async () => {
    if (!jwt) return;
    setRestosLoading(true);
    setRestosError(null);
    try {
      const json = await apiGet('/api/owner/restaurants');
      setRestos(json.restaurants || []);
    } catch (e) {
      setRestosError(e.message || 'Erreur chargement liste restos');
    } finally {
      setRestosLoading(false);
    }
  }, [jwt]);

  useEffect(() => { if (jwt) loadRestos(); }, [jwt, loadRestos]);

  // Filtre client-side (case-insensitive sur nom + ville).
  const filteredRestos = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return restos;
    return restos.filter(r =>
      (r.nom || '').toLowerCase().includes(q) ||
      (r.ville || '').toLowerCase().includes(q)
    );
  }, [restos, search]);

  // ------- Impersonation : swap token + redirect /dashboard -------
  // Sécu côté front (le serveur reste l'autorité) :
  //  1. Backup du JWT owner actuel dans revyo_owner_token AVANT le swap.
  //  2. Swap du JWT actif vers le token impersonation.
  //  3. Pose d'un marker revyo_impersonation pour le banner + handler 401.
  //  4. Suppression de revyo_current_restaurant_id (un X-Restaurant-Id
  //     périmé pointerait sur un resto de l'owner, pas la cible).
  //  5. Si l'appel POST /impersonate échoue → on NE swap PAS : l'owner
  //     reste dans son identité native, on affiche l'erreur.
  const handleImpersonate = useCallback(async (restoId) => {
    if (!restoId || impersonating) return;
    setImpersonating(true);
    setImpersonateError(null);
    try {
      const resp = await apiPost(`/api/owner/impersonate/${restoId}`);
      if (!resp || !resp.token || !resp.restaurant) {
        throw new Error('Réponse impersonation invalide');
      }
      const ownerTok = await storage.get('token');
      if (ownerTok) await storage.set('revyo_owner_token', ownerTok);
      await storage.set('token', resp.token);
      await storage.set('revyo_impersonation', JSON.stringify({
        id: resp.restaurant.id,
        nom: resp.restaurant.nom,
        expiresAt: resp.expiresAt
      }));
      try { localStorage.removeItem('revyo_current_restaurant_id'); } catch {}
      navigate('/dashboard');
    } catch (e) {
      // Aucun swap effectué → l'owner reste connecté en owner.
      setImpersonateError(e.message || 'Impossible de démarrer le pilotage');
    } finally {
      setImpersonating(false);
    }
  }, [navigate, impersonating]);

  // ------- Chart (note history) -------
  useEffect(() => {
    // ⚠️ activeTab est en dépendance : le canvas n'existe pas tant que l'onglet
    // Technique n'est pas ouvert. Sans cela, chartRef.current est null au
    // moment du fetch et le graphique ne s'affichait jamais.
    if (!data || !chartRef.current) return;
    const history = data.note_history_30d || [];
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();
    if (history.length === 0) return;
    const labels = history.map(p =>
      new Date(p.day).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    );
    const values = history.map(p => parseFloat(p.avg_note));
    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Note Google moyenne',
          data: values,
          borderColor: tokens.colors.brand,
          backgroundColor: tokens.colors.brandLight,
          fill: true,
          tension: 0.3,
          pointRadius: 3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { min: 3.5, max: 5, grid: { color: tokens.colors.sandBorder } },
          x: { grid: { display: false } }
        }
      }
    });
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data, activeTab]);

  function logout() {
    if (!window.confirm('Te déconnecter de cet appareil ?')) return;
    try { localStorage.removeItem(JWT_KEY); } catch {}
    setJwt('');
    setData(null);
    setError(null);
    navigate('/login');
  }

  // ------- 403 : connecté mais pas owner -------
  if (forbidden) {
    return (
      <div style={S.page}>
        <div style={S.setupCard}>
          <h1 style={S.setupTitle}>Accès réservé aux owners</h1>
          <p style={S.setupText}>
            Cette page est réservée à Florian et Adrien (équipe fondatrice Revyo).
            Si tu penses qu'il y a une erreur, contacte florian@getrevyo.com.
          </p>
          <button onClick={() => navigate('/dashboard')} style={S.btnPrimary}>
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  // ------- Pas connecté : on rend rien (useEffect redirige vers /login) -------
  if (!jwt) return null;

  // ------- Main dashboard -------
  const b = data?.business || {};
  const f = data?.funnel || {};
  const t = data?.tech || {};
  const top = data?.top_restos || [];
  const hist = data?.note_history_30d || [];
  const byLang = data?.funnel_by_lang_30d || [];

  return (
    <div style={S.page} className="ow-page">
      <style>{MOBILE_CSS}</style>
      <div style={S.wrap}>

        <header style={S.header}>
          <div style={S.titleRow}>
            <div style={S.logo}>R</div>
            <h1 style={S.title}>Owner Dashboard</h1>
            <span style={S.meta}>
              {lastFetchAt
                ? `MAJ ${lastFetchAt.toLocaleTimeString('fr-FR')}${data?.generation_ms ? ` · ${data.generation_ms}ms` : ''}`
                : 'Chargement…'}
            </span>
          </div>
          <div style={S.actions}>
            <button onClick={loadData} disabled={loading} style={S.btn}>
              {loading ? '↻ ...' : '↻ Rafraîchir'}
            </button>
            <button onClick={logout} style={S.btnGhost}>Se déconnecter</button>
          </div>
        </header>

        {error && (
          <div style={S.errorBox}>
            <strong>Erreur</strong> — {error}
          </div>
        )}

        {!data && !error && (
          <div style={S.loading}>Chargement des métriques Revyo…</div>
        )}

        {data && (
          <>
            {/* Barre d'onglets */}
            <nav style={S.tabBar} className="ow-tabbar" role="tablist" aria-label="Sections du dashboard owner">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => selectTab(tab.id)}
                  style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : null) }}
                >
                  <span aria-hidden="true">{tab.emoji}</span> {tab.label}
                </button>
              ))}
            </nav>

            {/* ───────────── VUE D'ENSEMBLE ───────────── */}
            {activeTab === 'apercu' && (
            <>
            {/* Hero cards */}
            <div style={S.heroes} className="ow-heroes">
              {/* Le sous-titre listait starter/pro/premium — trois formules qui
                  n'existent plus. On affiche la répartition réelle renvoyée par
                  l'API, quelles que soient les formules en vigueur. */}
              <Hero
                label="MRR estimé"
                value={fmtEur(b.mrr_eur_estimate)}
                sub={Object.entries(b.plan_breakdown || {})
                  .filter(([, n]) => n > 0)
                  .map(([plan, n]) => `${n} ${plan}`)
                  .join(' · ') || 'aucun abonnement actif'}
                accent="brand"
              />
              <Hero label="Restos actifs (30j)" value={fmtNum(b.restos_active_30d)} sub={`sur ${fmtNum(b.restos_total)} inscrits`} />
              <Hero label="Avis potentiels (30j)" value={fmtNum(f.avis_potentiels_30d)} sub={`conversion ${fmtPct(f.taux_conversion_qr_to_review_pct)}`} accent="green" />
              <Hero label="Taux de satisfaction" value={fmtPct(f.taux_satisfaction_pct)} sub={`${fmtNum(f.scans_30d)} scans QR sur 30j`} />
            </div>

            <AlertesBlock data={data} onGoTo={selectTab} />
            </>
            )}

            {/* ───────────── ONGLETS DÉDIÉS ───────────── */}
            {activeTab === 'sante'    && <SanteBlock s={data.sante} />}
            {activeTab === 'activite' && (
              <>
                <ActiviteBlock a={data.activite} />
                <Funnel f={f} />
                <FunnelByLang rows={byLang} />
              </>
            )}
            {activeTab === 'revenu'   && (
              <>
                <ConversionBlock c={data.conversion} />
                <CoutsBlock c={data.couts} />
                <BusinessDetail b={b} />
              </>
            )}
            {activeTab === 'comptes'  && <GestionComptes />}

            {/* ───────────── TECHNIQUE ───────────── */}
            {activeTab === 'technique' && (
            <>
            {/* Hermes block */}
            <div style={S.hermesBlock}>
              <h3 style={S.h3}>📋 Tâches Hermes — accessible en local</h3>
              <p style={S.hermesText}>
                Les tâches owner sont stockées sur le Mac de Florian (RGPD-friendly, pas en base).
                Pour les voir, ouvre un terminal et tape :
              </p>
              <div style={S.cmdRow}>
                <code style={S.code}>hermes task list</code>
                <code style={S.code}>hermes task prioritize</code>
                <code style={S.code}>hermes daily-brief</code>
              </div>
              <p style={S.hermesNote}>
                Tâches partagées avec Adrien → roadmap : sync Postgres + section live ici.
              </p>
            </div>

            {/* Chart + Tech */}
            <div style={S.rowEqual} className="ow-row">
              <div style={S.card} className="ow-card">
                <h3 style={S.h3}>Évolution note Google (moyenne tous restos, 30j)</h3>
                <div style={S.chartBox}>
                  <canvas ref={chartRef} />
                </div>
                {hist.length === 0 && (
                  <p style={S.subnote}>
                    Table <code style={S.codeMini}>note_history</code> vide ou absente — sera populée par le cron Outscraper.
                  </p>
                )}
              </div>
              <TechHealth t={t} />
            </div>

            </>
            )}

            {/* ───────────── PILOTER UN RESTAURANT ───────────── */}
            {activeTab === 'piloter' && (
            <>
            {/* Top restos (lignes cliquables → handleImpersonate) */}
            <TopRestos top={top} onPilot={handleImpersonate} disabled={impersonating} />

            {/* Section Pilotage : liste complète + recherche */}
            <PilotSection
              restos={filteredRestos}
              total={restos.length}
              loading={restosLoading}
              error={restosError}
              search={search}
              setSearch={setSearch}
              onPilot={handleImpersonate}
              impersonating={impersonating}
              impersonateError={impersonateError}
              onRefresh={loadRestos}
            />
            </>
            )}

            <footer style={S.footer}>
              Owner Dashboard Revyo · données live depuis Railway · auto-refresh 60s · v0.1
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Sous-composants ----------

/* ═══════════════════════════════════════════════════════════════════════════
 * Blocs ajoutés le 31/07/2026 : activité, conversion, coûts, gestion comptes
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Appel authentifié aux routes owner. Le JWT est celui déjà stocké par la page
 * (même clé localStorage), donc pas de second mécanisme d'auth à maintenir.
 * Les codes d'erreur métier remontent dans err.code pour un message adapté.
 */
async function ownerFetch(path, options = {}) {
  let token = '';
  try { token = localStorage.getItem(JWT_KEY) || ''; } catch { /* storage indispo */ }
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(payload.message || payload.error || `Erreur ${res.status}`);
    err.code = payload.error;
    err.status = res.status;
    throw err;
  }
  return payload;
}

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

/**
 * Santé des comptes — repérer qui va partir avant qu'il parte.
 *
 * « jamais activé » et « décroché » sont séparés à dessein : le premier est un
 * échec d'onboarding (le client n'a jamais vu la valeur), le second un churn en
 * cours (il l'a vue puis s'est arrêté). Les actions à mener sont opposées.
 */
/**
 * Encart « À traiter » de la vue d'ensemble.
 *
 * Ne liste que ce sur quoi on peut AGIR aujourd'hui, chaque ligne renvoyant
 * vers l'onglet qui permet de le traiter. Une alerte qui n'appelle aucune
 * action n'a rien à faire ici — elle irait grossir le bruit et ferait perdre
 * l'habitude de regarder ce bloc.
 */
function AlertesBlock({ data, onGoTo }) {
  const s = data.sante || {};
  const c = data.conversion || {};

  const alertes = [
    {
      cle: 'essais',
      n: c.essais_expirent_3j || 0,
      texte: (n) => `${n} essai${n > 1 ? 's' : ''} se termine${n > 1 ? 'nt' : ''} sous 3 jours`,
      quoi: 'Relancer avant la bascule en pause',
      onglet: 'revenu',
    },
    {
      cle: 'decroches',
      n: s.decroches_30d || 0,
      texte: (n) => `${n} compte${n > 1 ? 's' : ''} décroché${n > 1 ? 's' : ''} depuis 30 jours`,
      quoi: 'Utilisaient le produit puis se sont arrêtés',
      onglet: 'sante',
    },
    {
      cle: 'sans_google',
      n: s.sans_fiche_google || 0,
      texte: (n) => `${n} restaurant${n > 1 ? 's' : ''} sans fiche Google reliée`,
      quoi: 'Le produit ne peut pas fonctionner sans elle',
      onglet: 'sante',
    },
    {
      cle: 'jamais',
      n: s.jamais_active || 0,
      texte: (n) => `${n} compte${n > 1 ? 's' : ''} n'${n > 1 ? 'ont' : 'a'} jamais eu de scan`,
      quoi: 'Onboarding à reprendre',
      onglet: 'sante',
    },
  ].filter((a) => a.n > 0);

  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>⚠️ À traiter</h3>
      {alertes.length === 0 ? (
        <p style={S.note}>Rien à signaler : aucun essai qui expire, aucun compte décroché.</p>
      ) : (
        <div>
          {alertes.map((a) => (
            <button
              key={a.cle}
              type="button"
              onClick={() => onGoTo(a.onglet)}
              style={S.alerteRow}
            >
              <span style={S.alerteNum}>{a.n}</span>
              <span style={S.alerteTexte}>
                <strong>{a.texte(a.n)}</strong>
                <span style={S.tdSub}>{a.quoi}</span>
              </span>
              <span style={S.alerteChevron} aria-hidden="true">›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SanteBlock({ s }) {
  if (!s) return null;
  const motifLabel = { jamais_active: 'Jamais activé', decroche: 'Décroché' };
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>🩺 Santé des comptes</h3>
      <div style={S.miniGrid} className="ow-minigrid">
        <Mini label="Actifs 30j" value={fmtNum(s.actifs_30d)} />
        <Mini label="Jamais activés" value={fmtNum(s.jamais_active)} alert={s.jamais_active > 0} />
        <Mini label="Décrochés 30j" value={fmtNum(s.decroches_30d)} alert={s.decroches_30d > 0} />
        <Mini label="Sans fiche Google" value={fmtNum(s.sans_fiche_google)} alert={s.sans_fiche_google > 0} />
        <Mini label="Onboarding raté"
              value={s.taux_jamais_active_pct == null ? '—' : `${s.taux_jamais_active_pct}%`}
              alert={s.taux_jamais_active_pct > 50} />
      </div>
      <p style={S.note}>
        « Jamais activé » = QR jamais scanné depuis l'inscription : l'onboarding a
        échoué. « Décroché » = le QR a servi puis plus rien depuis 30 jours : churn
        en cours. Sans fiche Google reliée, le produit ne peut pas fonctionner.
      </p>
      {s.restos_a_risque?.length > 0 && (
        <table style={S.table} className="ow-table">
          <thead>
            <tr><th style={S.th}>Restaurant</th><th style={S.th}>Motif</th>
                <th style={S.th}>Dernier scan</th><th style={S.th}>Scans</th>
                <th style={S.th}>Fiche Google</th></tr>
          </thead>
          <tbody>
            {s.restos_a_risque.map((r, i) => (
              <tr key={`${r.email}-${i}`}>
                <td style={S.td} data-label="Restaurant">{r.nom}{r.ville ? ` · ${r.ville}` : ''}<br />
                  <span style={S.tdSub}>{r.email}</span></td>
                <td style={S.td} data-label="Motif">{motifLabel[r.motif] || r.motif}</td>
                <td style={S.td} data-label="Dernier scan">{r.dernier_scan ? fmtDate(r.dernier_scan) : '—'}</td>
                <td style={S.td} data-label="Scans">{fmtNum(r.scans_total)}</td>
                <td style={S.td} data-label="Fiche Google">{r.a_fiche_google ? 'oui' : '⚠️ non'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActiviteBlock({ a }) {
  if (!a) return null;
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>👥 Activité & connexions</h3>
      <div style={S.miniGrid} className="ow-minigrid">
        <Mini label="Connectés 7j" value={fmtNum(a.connectes_7j)} />
        <Mini label="Connectés 30j" value={fmtNum(a.connectes_30d)} />
        <Mini label="Jamais connectés" value={fmtNum(a.jamais_connectes)} />
        <Mini label="Dernière connexion" value={fmtDate(a.derniere_connexion)} />
      </div>
      {/* Sans ce repère, « jamais connectés » est ininterprétable : les comptes
          antérieurs au suivi y figurent tant qu'ils ne se sont pas reconnectés. */}
      <p style={S.note}>
        Suivi des connexions démarré le {a.tracking_depuis}. Les comptes plus anciens
        apparaissent comme « jamais connectés » jusqu'à leur prochaine connexion.
      </p>
      {a.derniers_logins?.length > 0 && (
        <table style={S.table} className="ow-table">
          <thead>
            <tr><th style={S.th}>Compte</th><th style={S.th}>Dernière connexion</th>
                <th style={S.th}>Connexions</th><th style={S.th}>Statut</th></tr>
          </thead>
          <tbody>
            {a.derniers_logins.map((u) => (
              <tr key={u.email}>
                <td style={S.td} data-label="Compte">{[u.prenom, u.nom].filter(Boolean).join(' ') || '—'}<br />
                  <span style={S.tdSub}>{u.email}</span></td>
                <td style={S.td} data-label="Dernière connexion">{fmtDate(u.last_login_at)}</td>
                <td style={S.td} data-label="Connexions">{fmtNum(u.login_count)}</td>
                <td style={S.td} data-label="Statut">{u.account_status}{u.plan ? ` · ${u.plan}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ConversionBlock({ c }) {
  if (!c) return null;
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>💳 Revenu & conversion</h3>
      <div style={S.miniGrid} className="ow-minigrid">
        <Mini label="Payants" value={fmtNum(c.payants)} />
        <Mini label="Essais en cours" value={fmtNum(c.essais_en_cours)} />
        <Mini label="Essais finissant sous 3j" value={fmtNum(c.essais_expirent_3j)}
              alert={c.essais_expirent_3j > 0} />
        <Mini label="Essai → payant" value={c.taux_essai_vers_payant_pct == null ? '—' : `${c.taux_essai_vers_payant_pct}%`} />
        <Mini label="En pause" value={fmtNum(c.en_pause)} />
        <Mini label="Supprimés" value={fmtNum(c.supprimes)} />
      </div>
      <p style={S.note}>
        Le taux essai → payant rapporte les payants aux comptes réellement sortis de
        l'essai (payants + en pause) — les essais en cours n'y entrent pas.
      </p>
    </div>
  );
}

function CoutsBlock({ c }) {
  if (!c) return null;
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>💸 Coûts variables (30j)</h3>
      <div style={S.miniGrid} className="ow-minigrid">
        <Mini label="SMS envoyés" value={fmtNum(c.sms_envoyes_30d)} />
        <Mini label="Insights générés" value={fmtNum(c.insights_generes_30d)} />
        <Mini label="Réponses IA" value={fmtNum(c.reponses_ia_30d)} />
      </div>
      {c.sms_top_restos?.length > 0 && (
        <table style={S.table} className="ow-table">
          <thead><tr><th style={S.th}>Restaurant</th><th style={S.th}>SMS (30j)</th></tr></thead>
          <tbody>
            {c.sms_top_restos.map((r) => (
              <tr key={r.nom}><td style={S.td} data-label="Restaurant">{r.nom}</td><td style={S.td} data-label="SMS (30j)">{fmtNum(r.sms_30d)}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Mini({ label, value, alert }) {
  return (
    <div style={S.mini}>
      <div style={S.miniLabel}>{label}</div>
      <div style={{ ...S.miniValue, ...(alert ? { color: tokens.colors.brand } : null) }}>{value}</div>
    </div>
  );
}

/**
 * Gestion des comptes : recherche, suppression réversible et purge définitive.
 * La confirmation impose de RECOPIER l'e-mail du compte visé — un clic
 * accidentel ne peut pas suffire pour une action destructive.
 */
function GestionComptes() {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // { user, mode }
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (q = '') => {
    setLoading(true); setMsg(null);
    try {
      const r = await ownerFetch(`/api/owner/users?search=${encodeURIComponent(q)}`);
      setUsers(r.users || []);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Chargement impossible' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(''); }, []);

  const run = async () => {
    const { user, mode } = confirmFor;
    setBusy(true); setMsg(null);
    try {
      const path = mode === 'purge' ? 'purge' : 'soft-delete';
      const body = { confirm_email: confirmEmail.trim() };
      if (mode === 'purge') body.confirm_purge = true;
      await ownerFetch(`/api/owner/users/${user.id}/${path}`, { method: 'POST', body: JSON.stringify(body) });
      setMsg({ type: 'ok', text: mode === 'purge'
        ? `Compte ${user.email} purgé définitivement.`
        : `Compte ${user.email} supprimé (récupérable 6 mois).` });
      setConfirmFor(null); setConfirmEmail('');
      await load(search);
    } catch (e) {
      const map = {
        confirmation_email_mismatch: "L'e-mail saisi ne correspond pas au compte.",
        cannot_delete_owner: 'Impossible de supprimer un compte owner.',
        active_subscription: "Ce compte a un abonnement Stripe actif : résiliez-le d'abord dans Stripe.",
        already_deleted: 'Ce compte est déjà supprimé.',
        purge_confirmation_required: 'Confirmation de purge manquante.',
      };
      setMsg({ type: 'error', text: map[e.code] || e.message || 'Échec' });
    } finally { setBusy(false); }
  };

  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>🗂️ Gestion des comptes</h3>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(search); }}
          placeholder="Rechercher un e-mail, un nom, un restaurant…"
          style={S.input}
          className="ow-input"
        />
        <button type="button" onClick={() => load(search)} style={S.btn} className="ow-fullwidth" disabled={loading}>
          {loading ? '…' : 'Rechercher'}
        </button>
      </div>

      {msg && (
        <div style={msg.type === 'error' ? S.msgError : S.msgOk}>{msg.text}</div>
      )}

      <table style={S.table} className="ow-table">
        <thead>
          <tr><th style={S.th}>Compte</th><th style={S.th}>Statut</th><th style={S.th}>Restaurants</th>
              <th style={S.th}>Dernière connexion</th><th style={S.th}>Actions</th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td style={S.td} data-label="Compte">
                {[u.prenom, u.nom].filter(Boolean).join(' ') || '—'}
                {u.is_internal_test && <span style={S.tag}>test</span>}
                <br /><span style={S.tdSub}>{u.email}</span>
              </td>
              <td style={S.td} data-label="Statut">
                {u.account_status}
                {u.has_subscription && <><br /><span style={S.tdSub}>abonné Stripe</span></>}
              </td>
              <td style={S.td} data-label="Restaurants">{u.nb_restaurants > 0 ? u.restaurants : '—'}</td>
              <td style={S.td} data-label="Dernière connexion">{fmtDate(u.last_login_at)}</td>
              <td style={S.td} data-label="Actions">
                {u.account_status !== 'deleted' && (
                  <button type="button" style={S.btnSmall}
                          onClick={() => { setConfirmFor({ user: u, mode: 'soft' }); setConfirmEmail(''); setMsg(null); }}>
                    Supprimer
                  </button>
                )}
                <button type="button" style={{ ...S.btnSmall, ...S.btnDanger }}
                        onClick={() => { setConfirmFor({ user: u, mode: 'purge' }); setConfirmEmail(''); setMsg(null); }}>
                  Purger
                </button>
              </td>
            </tr>
          ))}
          {users.length === 0 && !loading && (
            <tr><td style={S.td} colSpan={5}>Aucun compte trouvé.</td></tr>
          )}
        </tbody>
      </table>

      {confirmFor && (
        <div style={S.confirmBox}>
          <strong>
            {confirmFor.mode === 'purge'
              ? '⚠️ Purge DÉFINITIVE — irréversible'
              : 'Suppression réversible (6 mois de rétention)'}
          </strong>
          <p style={{ margin: '8px 0', fontSize: 13 }}>
            {confirmFor.mode === 'purge'
              ? `Toutes les données de ${confirmFor.user.email} et de ses restaurants seront effacées sans retour possible.`
              : `Les données personnelles de ${confirmFor.user.email} seront anonymisées. Récupérable pendant 6 mois.`}
          </p>
          <p style={{ margin: '8px 0', fontSize: 13 }}>
            Recopiez <code>{confirmFor.user.email}</code> pour confirmer :
          </p>
          <input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                 placeholder={confirmFor.user.email} style={S.input} className="ow-input" />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={run} disabled={busy || !confirmEmail}
                    style={{ ...S.btn, ...(confirmFor.mode === 'purge' ? S.btnDanger : null) }}>
              {busy ? '…' : (confirmFor.mode === 'purge' ? 'Purger définitivement' : 'Supprimer')}
            </button>
            <button type="button" onClick={() => { setConfirmFor(null); setConfirmEmail(''); }} style={S.btn}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Hero({ label, value, sub, accent }) {
  const valueColor =
    accent === 'brand' ? tokens.colors.brand
    : accent === 'green' ? tokens.colors.green
    : tokens.colors.ink;
  return (
    <div style={S.hero}>
      <div style={S.heroLabel}>{label}</div>
      <div style={{ ...S.heroValue, color: valueColor }}>{value}</div>
      <div style={S.heroSub}>{sub}</div>
    </div>
  );
}

function Funnel({ f }) {
  const totalSat =
    (f.satisfaction_breakdown?.excellent || 0) +
    (f.satisfaction_breakdown?.bien || 0) +
    (f.satisfaction_breakdown?.peut_mieux_faire || 0);
  const pct = (v) => totalSat > 0 ? `(${Math.round(v / totalSat * 100)}%)` : '';
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>Funnel QR → Avis Google (30j)</h3>
      <div style={S.funnel}>
        <FunnelStep label="Scans QR" value={fmtNum(f.scans_30d)} />
        <div style={S.funnelArrow}>↓</div>
        <FunnelStep label="😊 Excellents" value={fmtNum(f.satisfaction_breakdown?.excellent || 0)} pct={pct(f.satisfaction_breakdown?.excellent || 0)} />
        <FunnelStep label="👍 Bien" value={fmtNum(f.satisfaction_breakdown?.bien || 0)} pct={pct(f.satisfaction_breakdown?.bien || 0)} />
        <FunnelStep label="😐 Peut mieux faire" value={fmtNum(f.satisfaction_breakdown?.peut_mieux_faire || 0)} pct={pct(f.satisfaction_breakdown?.peut_mieux_faire || 0)} />
        <div style={S.funnelArrow}>↓</div>
        <FunnelStep label="Avis Google potentiels" value={fmtNum(f.avis_potentiels_30d)} highlight />
      </div>
    </div>
  );
}

function FunnelByLang({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>🌍 Funnel par langue (30j)</h3>
      <div style={S.langTableWrap}>
        <table style={S.langTable}>
          <thead>
            <tr>
              <th style={S.langTh}>Langue</th>
              <th style={S.langThR}>Scans</th>
              <th style={S.langThR}>Excellents</th>
              <th style={S.langThR}>Taux ⭐ (%)</th>
              <th style={S.langThR}>Clics Google</th>
              <th style={S.langThR}>Conv Google (%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.lang || '_'}>
                <td style={S.langTd}>{flagFor(row.lang)} {labelFor(row.lang)}</td>
                <td style={S.langTdR}>{fmtNum(row.scans)}</td>
                <td style={S.langTdR}>{fmtNum(row.excellents)}</td>
                <td style={S.langTdR}>{row.taux_excellent_pct ?? '—'}{row.taux_excellent_pct != null ? '%' : ''}</td>
                <td style={S.langTdR}>{fmtNum(row.google_clics)}</td>
                <td style={S.langTdR}>{row.taux_google_clic_pct ?? '—'}{row.taux_google_clic_pct != null ? '%' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={S.langHint}>
        💡 Si une langue affiche beaucoup de scans mais peu de clics Google, le funnel mérite un audit (UX, copy, longueur du flow).
      </p>
    </div>
  );
}

function FunnelStep({ label, value, pct, highlight }) {
  return (
    <div style={{
      ...S.funnelStep,
      background: highlight ? tokens.colors.greenLight : tokens.colors.sand2
    }}>
      <span style={S.funnelLabel}>{label}</span>
      <span>
        <span style={{
          ...S.funnelValue,
          color: highlight ? tokens.colors.green : tokens.colors.ink
        }}>{value}</span>
        {pct && <span style={S.funnelPct}> {pct}</span>}
      </span>
    </div>
  );
}

function BusinessDetail({ b }) {
  const taux = b.restos_total > 0
    ? Math.round((b.restos_active_30d / b.restos_total) * 100) + '%'
    : '—';
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>Croissance utilisateurs</h3>
      <div style={S.kv}>
        <KV k="Inscriptions totales" v={fmtNum(b.users_total)} />
        <KV k="Nouveaux (30j)" v={fmtNum(b.users_30d)} />
        <KV k="Restaurants total" v={fmtNum(b.restos_total)} />
        <KV k="Restos actifs (30j)" v={fmtNum(b.restos_active_30d)} />
        <KV k="Taux activation" v={taux} />
      </div>
    </div>
  );
}

function TechHealth({ t }) {
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>Tech health</h3>
      <div style={S.kv}>
        <KV k="SMS envoyés (30j)" v={t.sms_sent_30d == null ? 'table absente' : fmtNum(t.sms_sent_30d)} muted={t.sms_sent_30d == null} />
        <KV k="Feedbacks négatifs (30j)" v={t.feedbacks_negatifs_30d == null ? 'table absente' : fmtNum(t.feedbacks_negatifs_30d)} muted={t.feedbacks_negatifs_30d == null} />
        <KV k="Dernier scan QR" v={relTime(t.last_scan_at)} />
        <KV k="Dernier SMS" v={relTime(t.last_sms_at)} />
        <KV k="DB Postgres" v={<Badge tone="green">OK</Badge>} />
      </div>
    </div>
  );
}

function KV({ k, v, muted }) {
  return (
    <>
      <span style={S.kvKey}>{k}</span>
      <span style={{ ...S.kvValue, color: muted ? tokens.colors.gray3 : tokens.colors.ink }}>
        {v}
      </span>
    </>
  );
}

function Badge({ tone, children }) {
  const map = {
    green: { bg: tokens.colors.greenLight, fg: tokens.colors.green },
    amber: { bg: '#FBF3DE', fg: tokens.colors.gold },
    red: { bg: tokens.colors.redLight, fg: tokens.colors.red }
  };
  const c = map[tone] || map.green;
  return (
    <span style={{ ...S.badge, background: c.bg, color: c.fg }}>{children}</span>
  );
}

function TopRestos({ top, onPilot, disabled }) {
  return (
    <div style={S.card} className="ow-card">
      <h3 style={S.h3}>Top 5 restaurants (par activité 30j)</h3>
      <p style={{ fontSize: 13, color: tokens.colors.gray1, marginTop: -6, marginBottom: 12 }}>
        Cliquer sur une ligne pour piloter ce compte.
      </p>
      {top.length === 0 ? (
        <div style={S.emptyState}>Pas encore de restaurants actifs sur les 30 derniers jours</div>
      ) : (
        <table style={S.table} className="ow-table">
          <thead>
            <tr>
              <th style={S.th}>Restaurant</th>
              <th style={S.th}>Ville</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Scans</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Avis potentiels</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Satisfaction</th>
            </tr>
          </thead>
          <tbody>
            {top.map(r => {
              let satBadge = '—';
              if (r.taux_satisfaction != null) {
                const tone =
                  r.taux_satisfaction >= 85 ? 'green'
                  : r.taux_satisfaction >= 60 ? 'amber'
                  : 'red';
                satBadge = <Badge tone={tone}>{r.taux_satisfaction}%</Badge>;
              }
              return (
                <tr
                  key={r.id}
                  onClick={() => !disabled && onPilot && onPilot(r.id)}
                  style={{
                    cursor: disabled ? 'wait' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                  }}
                  title="Cliquer pour piloter ce compte"
                >
                  <td style={S.td} data-label="Restaurant"><strong>{r.nom}</strong></td>
                  <td style={S.td} data-label="Ville">{r.ville || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right' }} data-label="Scans">{fmtNum(r.scans_30d)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }} data-label="Avis potentiels">{fmtNum(r.avis_potentiels_30d)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }} data-label="Satisfaction">{satBadge}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// =====================================================================
// PilotSection — liste complète des restos pour démarrer un pilotage.
// =====================================================================
function PilotSection({
  restos, total, loading, error, search, setSearch,
  onPilot, impersonating, impersonateError, onRefresh
}) {
  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ ...S.h3, margin: 0 }}>🎛️ Piloter un restaurant</h3>
        <button onClick={onRefresh} disabled={loading} style={S.btn}>
          {loading ? '↻ ...' : '↻ Actualiser'}
        </button>
      </div>

      <p style={{ fontSize: 13, color: tokens.colors.gray1, marginTop: 6 }}>
        Ouvre le dashboard du restaurant cible en mode pilotage (JWT 2h, actions auditées).
        Suppression de compte et changement de mot de passe sont désactivés en pilotage.
      </p>

      <input
        type="text"
        className="ow-input"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Filtrer par nom ou ville…"
        style={{
          width: '100%',
          padding: '10px 12px',
          marginTop: 8,
          marginBottom: 12,
          border: `1px solid ${tokens.colors.sandBorder}`,
          borderRadius: 6,
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      />

      {error && (
        <div style={{ ...S.errorBox, marginBottom: 12 }}>
          <strong>Erreur</strong> — {error}
        </div>
      )}

      {impersonateError && (
        <div style={{ ...S.errorBox, marginBottom: 12 }}>
          <strong>Pilotage refusé</strong> — {impersonateError}
        </div>
      )}

      {loading && restos.length === 0 ? (
        <div style={S.loading}>Chargement…</div>
      ) : restos.length === 0 ? (
        <div style={S.emptyState}>
          {search ? 'Aucun resto ne correspond à ce filtre.' : 'Aucun restaurant en base.'}
        </div>
      ) : (
        <>
          <table style={S.table} className="ow-table">
            <thead>
              <tr>
                <th style={S.th}>Restaurant</th>
                <th style={S.th}>Ville</th>
                <th style={S.th}>Statut compte</th>
                <th style={S.th}>Google</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {restos.map(r => (
                <tr key={r.id}>
                  <td style={S.td} data-label="Restaurant"><strong>{r.nom}</strong></td>
                  <td style={S.td} data-label="Ville">{r.ville || '—'}</td>
                  <td style={S.td} data-label="Statut compte">
                    <Badge tone={accountStatusTone(r.account_status)}>
                      {r.account_status || '—'}
                    </Badge>
                  </td>
                  <td style={S.td} data-label="Google">{r.has_google ? '✓' : '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right' }} data-label="Action">
                    <button
                      onClick={() => onPilot(r.id)}
                      disabled={impersonating}
                      style={S.btn}
                    >
                      {impersonating ? '...' : 'Piloter →'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {search && (
            <p style={{ fontSize: 12, color: tokens.colors.gray1, marginTop: 8 }}>
              {restos.length} / {total} restaurants
            </p>
          )}
        </>
      )}
    </div>
  );
}

function accountStatusTone(status) {
  if (status === 'active') return 'green';
  if (status === 'trial') return 'amber';
  if (status === 'paused' || status === 'expired' || status === 'deleted') return 'red';
  return 'amber';
}

// ---------- Formatters ----------

function fmtEur(n) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0
  }).format(n || 0);
}
function fmtNum(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}
function fmtPct(n) {
  if (n == null) return '—';
  return `${n}%`;
}
function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min} min`;
  if (min < 60 * 24) return `il y a ${Math.round(min / 60)} h`;
  return `il y a ${Math.round(min / 1440)} j`;
}

// ---------- Styles (inline, design-tokens) ----------

const S = {
  // --- Onglets (01/08/2026) ---
  tabBar: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18,
            paddingBottom: 10, borderBottom: '1px solid rgba(0,0,0,0.08)' },
  tab: { padding: '8px 14px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.12)',
         background: '#FFF', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
         color: '#55524A', cursor: 'pointer', whiteSpace: 'nowrap' },
  tabActive: { background: '#18160F', color: '#FFF', borderColor: '#18160F' },
  // --- Encart « À traiter » ---
  alerteRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%',
               padding: '10px 8px', border: 'none', borderBottom: '1px solid rgba(0,0,0,0.06)',
               background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  alerteNum: { minWidth: 34, fontSize: 19, fontWeight: 800, color: '#C8442A' },
  alerteTexte: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, fontSize: 13.5, color: '#18160F' },
  alerteChevron: { fontSize: 20, color: '#8A867C' },
  // --- Blocs ajoutés le 31/07/2026 (activité, conversion, coûts, comptes) ---
  miniGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 },
  mini: { background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, padding: '10px 12px' },
  miniLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8A867C', marginBottom: 4 },
  miniValue: { fontSize: 20, fontWeight: 800, color: '#18160F' },
  note: { fontSize: 12, color: '#8A867C', lineHeight: 1.5, margin: '4px 0 12px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 },
  th: { textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid rgba(0,0,0,0.1)', fontSize: 11,
        textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8A867C', fontWeight: 700 },
  td: { padding: '10px 6px', borderBottom: '1px solid rgba(0,0,0,0.05)', verticalAlign: 'top' },
  tdSub: { fontSize: 11.5, color: '#8A867C' },
  tag: { marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 6, background: '#EEE', color: '#666' },
  input: { flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.16)',
           fontFamily: 'inherit', fontSize: 14 },
  btn: { padding: '9px 16px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.16)', background: '#FFF',
         fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnSmall: { padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.16)', background: '#FFF',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 },
  btnDanger: { background: '#C8442A', color: '#FFF', borderColor: '#C8442A' },
  confirmBox: { marginTop: 14, padding: 14, borderRadius: 10, background: '#FFF6F4', border: '1px solid #F0B3AC' },
  msgOk: { padding: '10px 12px', borderRadius: 8, background: '#EAF7EE', border: '1px solid #A9DBB8',
           color: '#1E6B36', fontSize: 13, marginBottom: 10 },
  msgError: { padding: '10px 12px', borderRadius: 8, background: '#FDECEA', border: '1px solid #F0B3AC',
              color: '#A32B1C', fontSize: 13, marginBottom: 10 },

  page: {
    minHeight: '100vh',
    background: tokens.colors.sand,
    color: tokens.colors.ink,
    fontFamily: tokens.fonts?.sans || "'Inter', system-ui, sans-serif",
    padding: 24,
    boxSizing: 'border-box'
  },
  wrap: { maxWidth: 1280, margin: '0 auto' },

  // Setup
  setupCard: {
    maxWidth: 480,
    margin: '80px auto',
    background: tokens.colors.white,
    padding: 32,
    borderRadius: 12,
    border: `1px solid ${tokens.colors.sandBorder}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  setupTitle: { fontSize: 22, fontWeight: 600, marginBottom: 8, letterSpacing: '-0.02em' },
  setupText: { color: tokens.colors.gray1, marginBottom: 20, fontSize: 14, lineHeight: 1.5 },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${tokens.colors.sandBorder}`,
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 12,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },

  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: {
    width: 32, height: 32,
    background: tokens.colors.brand,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colors.white,
    fontWeight: 700
  },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 },
  meta: { color: tokens.colors.gray3, fontSize: 12 },
  actions: { display: 'flex', gap: 8 },

  // Buttons
  btn: {
    background: tokens.colors.white,
    border: `1px solid ${tokens.colors.sandBorder}`,
    color: tokens.colors.ink,
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit'
  },
  btnPrimary: {
    background: tokens.colors.brand,
    color: tokens.colors.white,
    border: 'none',
    padding: '10px 18px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    width: '100%'
  },
  btnGhost: {
    background: 'transparent',
    border: 'none',
    color: tokens.colors.gray2,
    padding: '8px 14px',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit'
  },

  // States
  errorBox: {
    background: tokens.colors.errorBg,
    color: tokens.colors.errorText,
    padding: '12px 16px',
    borderRadius: 8,
    border: `1px solid ${tokens.colors.redBorder}`,
    marginBottom: 16,
    fontSize: 14
  },
  loading: { textAlign: 'center', padding: 60, color: tokens.colors.gray2 },

  // Heroes
  heroes: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 24
  },
  hero: {
    background: tokens.colors.white,
    padding: 20,
    borderRadius: 12,
    border: `1px solid ${tokens.colors.sandBorder}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  heroLabel: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens.colors.gray3,
    marginBottom: 8
  },
  heroValue: { fontSize: 32, fontWeight: 600, letterSpacing: '-0.03em', marginBottom: 4 },
  heroSub: { fontSize: 12, color: tokens.colors.gray1 },

  // Hermes block
  hermesBlock: {
    background: `linear-gradient(135deg, ${tokens.colors.brandLight} 0%, #FBF3DE 100%)`,
    padding: 20,
    borderRadius: 12,
    border: `1px solid ${tokens.colors.brandMid}`,
    marginBottom: 16
  },
  hermesText: { fontSize: 13, color: tokens.colors.gray1, marginBottom: 12, marginTop: 0 },
  cmdRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  hermesNote: { fontSize: 12, color: tokens.colors.gray3, marginTop: 8, marginBottom: 0 },

  // Cards / rows
  card: {
    background: tokens.colors.white,
    padding: 20,
    borderRadius: 12,
    border: `1px solid ${tokens.colors.sandBorder}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  rowMain: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: 16,
    marginBottom: 16
  },
  rowEqual: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16
  },
  h3: { fontSize: 14, fontWeight: 600, marginBottom: 16, marginTop: 0, letterSpacing: '-0.01em' },

  // Funnel
  funnel: { display: 'flex', flexDirection: 'column', gap: 12 },
  funnelStep: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: 8
  },
  funnelLabel: { fontSize: 13, color: tokens.colors.gray1 },
  funnelValue: { fontSize: 18, fontWeight: 600 },
  funnelPct: { fontSize: 11, color: tokens.colors.gray3, marginLeft: 6 },
  funnelArrow: { textAlign: 'center', color: tokens.colors.gray3, fontSize: 12, margin: '-4px 0' },

  // Funnel par langue (i18n observability)
  langTableWrap: {
    overflowX: 'auto',
    borderRadius: 8,
    border: `1px solid ${tokens.colors.sandBorder}`
  },
  langTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  langTh: {
    textAlign: 'left',
    padding: '10px 14px',
    background: tokens.colors.sand2,
    color: tokens.colors.gray2,
    fontWeight: 600,
    borderBottom: `1px solid ${tokens.colors.sandBorder}`
  },
  langThR: {
    textAlign: 'right',
    padding: '10px 14px',
    background: tokens.colors.sand2,
    color: tokens.colors.gray2,
    fontWeight: 600,
    borderBottom: `1px solid ${tokens.colors.sandBorder}`
  },
  langTd: {
    padding: '10px 14px',
    color: tokens.colors.ink,
    borderBottom: `1px solid ${tokens.colors.sandBorder}`
  },
  langTdR: {
    padding: '10px 14px',
    color: tokens.colors.ink,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    borderBottom: `1px solid ${tokens.colors.sandBorder}`
  },
  langHint: { color: tokens.colors.gray1, fontSize: 12, marginTop: 10 },

  // KV grid
  kv: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 },
  kvKey: { color: tokens.colors.gray2 },
  kvValue: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },

  // Table
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colors.sandBorder}`,
    fontWeight: 500,
    color: tokens.colors.gray3,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  td: { padding: '10px 12px', borderBottom: `1px solid ${tokens.colors.sandBorder}` },

  // Badge
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },

  // Code
  code: {
    background: 'rgba(0,0,0,0.06)',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'ui-monospace, Menlo, monospace'
  },
  codeMini: { background: 'rgba(0,0,0,0.06)', padding: '1px 6px', borderRadius: 3, fontSize: 11 },

  // Chart
  chartBox: { height: 220, position: 'relative' },
  subnote: { fontSize: 12, color: tokens.colors.gray3, marginTop: 8, textAlign: 'center' },

  // Empty / footer
  emptyState: { textAlign: 'center', color: tokens.colors.gray3, padding: 20, fontSize: 13 },
  footer: {
    textAlign: 'center',
    color: tokens.colors.gray3,
    fontSize: 11,
    marginTop: 24,
    paddingTop: 16,
    borderTop: `1px solid ${tokens.colors.sandBorder}`
  }
};
